import { useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, CheckCircle2, ClipboardCheck, Download, Languages, Play, ShieldAlert } from "lucide-react";
import { useApi } from "@ncp/api-client";
import { formatDate, formatDateTime, formatNumberNear, useLocale, useTranslation } from "@ncp/i18n";
import { Badge, Button, Card, CardContent, CardDescription, CardHeader, CardTitle, Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, EmptyState, Field, InlineAlert, NativeSelect, Skeleton, Textarea, cn, notifySaveFailed, useSave } from "@ncp/ui";
import { useAiAcceptance, useAiRisks, useLanguageParity, type AcceptanceFunctionRow, type AcceptanceOfficerRow, type AiRiskLevel, type AiRiskRow } from "./api";

const levelTone = { High: "danger", Limited: "warning", Minimal: "success" } as const;

/**
 * The register of AI risks: for each function the risks it carries and the safeguard that answers each, who approves its drafts,
 * the last review and when the next falls due. The model documentation brings this together with the instructions, tests and use.
 */
export function RiskRegisterTab() {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const api = useApi();
  const risks = useAiRisks();
  const [level, setLevel] = useState<"" | AiRiskLevel>("");
  const [open, setOpen] = useState<string | null>(null);
  const [reviewing, setReviewing] = useState<AiRiskRow | null>(null);
  const [downloading, setDownloading] = useState(false);
  const ar = locale === "ar";
  const rows = useMemo(() => (risks.data?.functions ?? []).filter((r) => !level || r.level === level), [risks.data, level]);

  const download = async () => {
    setDownloading(true);
    try {
      await api.download("/api/governance/model-docs/export", `ncp-ai-model-documentation-${new Date().toISOString().slice(0, 10)}.md`);
    } catch (e) {
      notifySaveFailed(e, t("misc.error"));
    } finally {
      setDownloading(false);
    }
  };

  if (risks.isLoading || !risks.data) return <Skeleton className="h-64" />;
  const d = risks.data;
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <p className="max-w-3xl text-sm text-muted-foreground">{t("admin:governance.risks.hint")}</p>
        <div className="flex flex-wrap items-center gap-2">
          {d.due > 0 ? <Badge variant="warning" dot>{t("admin:governance.risks.due", { count: d.due })}</Badge> : <Badge variant="success">{t("admin:governance.risks.allReviewed")}</Badge>}
          <NativeSelect value={level} onChange={(e) => setLevel(e.target.value as "" | AiRiskLevel)} aria-label={t("admin:governance.risks.level")}>
            <option value="">{t("admin:governance.risks.allLevels")}</option>
            {d.levels.map((l) => <option key={l} value={l}>{t(`admin:governance.risks.levels.${l}`)}</option>)}
          </NativeSelect>
          <Button variant="outline" loading={downloading} onClick={() => void download()}><Download /> {t("admin:governance.risks.download")}</Button>
        </div>
      </div>
      {!d.canReview ? <p className="text-xs text-muted-foreground">{t("admin:governance.risks.reviewerOnly")}</p> : null}
      <div className="grid gap-3 xl:grid-cols-2">
        {rows.map((r) => (
          <Card key={r.agentKey} className={cn(r.due && "border-warning-200")}>
            <CardHeader className="pb-2">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <CardTitle className="text-base">{ar ? r.agentNameAr : r.agentName}</CardTitle>
                  <CardDescription>
                    {r.owner ? t("admin:governance.risks.owner", { role: t(`roles.${r.owner}`, { defaultValue: r.owner }) }) : t(`admin:governance.agentCatalog.categories.${r.category}`, { defaultValue: r.category })}
                  </CardDescription>
                </div>
                <Badge variant={levelTone[r.level]} size="sm">{t(`admin:governance.risks.levels.${r.level}`)}</Badge>
              </div>
            </CardHeader>
            <CardContent className="space-y-2 text-xs">
              <div className="flex flex-wrap items-center gap-2">
                {r.review
                  ? <span>{t("admin:governance.risks.reviewedBy", { date: formatDate(r.review.reviewedAt, locale), name: r.review.reviewedByName })}</span>
                  : <span className="text-muted-foreground">{t("admin:governance.risks.notReviewed")}</span>}
                {r.review ? <Badge variant="outline" size="sm">{t("admin:governance.risks.residualIs", { level: t(`admin:governance.risks.levels.${r.review.residual}`) })}</Badge> : null}
                {r.due
                  ? <Badge variant="warning" size="sm" dot><AlertTriangle /> {t("admin:governance.risks.overdue")}</Badge>
                  : r.review ? <span className="text-muted-foreground">{t("admin:governance.risks.nextReview", { date: formatDate(r.review.nextReviewAt, locale) })}</span> : null}
              </div>
              <p className="text-muted-foreground">{t("admin:governance.risks.usage", { runs: r.runs30d, events: r.guardrails30d, blocked: r.blocked30d })}</p>
              {r.parityFindings.length > 0 ? (
                <div className="flex flex-wrap gap-1">{r.parityFindings.map((f) => <Badge key={f} variant="danger" size="sm"><Languages /> {t(`admin:governance.parity.findings.${f}`, { defaultValue: f })}</Badge>)}</div>
              ) : null}
              {r.review?.actions ? <p><span className="font-medium">{t("admin:governance.risks.actionsRequired")}:</span> {r.review.actions}</p> : null}
              <div className="flex items-center gap-2">
                <Button size="sm" variant="link" onClick={() => setOpen(open === r.agentKey ? null : r.agentKey)}>
                  {open === r.agentKey ? t("actions.hideDetails") : t("admin:governance.risks.showRisks", { count: r.risks.length })}
                </Button>
                {d.canReview ? <Button size="sm" variant="outline" className="ms-auto" onClick={() => setReviewing(r)}><ClipboardCheck /> {t("admin:governance.risks.record")}</Button> : null}
              </div>
              {open === r.agentKey ? (
                <ul className="space-y-1.5">
                  {r.risks.map((x) => (
                    <li key={x.key} className="rounded-lg border border-sand-200 p-2">
                      <p className="flex items-start gap-1.5 font-medium"><ShieldAlert className="mt-0.5 size-3.5 shrink-0 text-danger-600" /> {ar ? x.riskAr : x.risk}</p>
                      <p className="mt-1 flex items-start gap-1.5 text-muted-foreground"><CheckCircle2 className="mt-0.5 size-3.5 shrink-0 text-success-600" /> {ar ? x.safeguardAr : x.safeguard}</p>
                    </li>
                  ))}
                </ul>
              ) : null}
            </CardContent>
          </Card>
        ))}
      </div>
      <ReviewDialog key={reviewing?.agentKey ?? "none"} row={reviewing} levels={d.levels} onClose={() => setReviewing(null)} />
    </div>
  );
}

function ReviewDialog({ row, levels, onClose }: { row: AiRiskRow | null; levels: AiRiskLevel[]; onClose: () => void }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const api = useApi();
  const qc = useQueryClient();
  const [form, setForm] = useState(() => ({ level: row?.level ?? "Limited", residual: row?.review?.residual ?? "Minimal", notes: "", actions: "" }));
  const save = useSave({
    mutationFn: () => api.post(`/api/governance/risks/${row!.agentKey}/review`, { level: form.level, residual: form.residual, notes: form.notes.trim(), actions: form.actions.trim() || null }),
    saved: t("admin:governance.risks.saved"),
    onSuccess: () => { onClose(); void qc.invalidateQueries({ queryKey: ["governance", "risks"] }); },
  });
  const ready = form.notes.trim().length >= 20 && (form.residual !== "High" || form.actions.trim().length > 0);
  const options = levels.map((l) => <option key={l} value={l}>{t(`admin:governance.risks.levels.${l}`)}</option>);
  return (
    <Dialog open={row !== null} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent size="lg">
        <DialogHeader>
          <DialogTitle>{t("admin:governance.risks.recordTitle", { name: row ? (locale === "ar" ? row.agentNameAr : row.agentName) : "" })}</DialogTitle>
          <DialogDescription>{t("admin:governance.risks.recordHint")}</DialogDescription>
        </DialogHeader>
        <div className="grid gap-3 md:grid-cols-2">
          <Field label={t("admin:governance.risks.level")} hint={row ? t("admin:governance.risks.registerSays", { level: t(`admin:governance.risks.levels.${row.registerLevel}`) }) : undefined}>
            {(id) => <NativeSelect id={id} value={form.level} onChange={(e) => setForm({ ...form, level: e.target.value as AiRiskLevel })}>{options}</NativeSelect>}
          </Field>
          <Field label={t("admin:governance.risks.residual")}>
            {(id) => <NativeSelect id={id} value={form.residual} onChange={(e) => setForm({ ...form, residual: e.target.value as AiRiskLevel })}>{options}</NativeSelect>}
          </Field>
          <Field label={t("admin:governance.risks.notes")} hint={t("admin:governance.risks.notesHint")} required className="md:col-span-2">
            {(id) => <Textarea id={id} rows={4} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />}
          </Field>
          <Field label={t("admin:governance.risks.actions")} hint={t("admin:governance.risks.actionsHint")} required={form.residual === "High"} className="md:col-span-2">
            {(id) => <Textarea id={id} rows={2} value={form.actions} onChange={(e) => setForm({ ...form, actions: e.target.value })} />}
          </Field>
          <p className="text-xs text-muted-foreground md:col-span-2">{t(form.level === "High" ? "admin:governance.risks.nextHigh" : "admin:governance.risks.nextOther")}</p>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>{t("actions.cancel")}</Button>
          <Button variant="gold" disabled={!ready} loading={save.isPending} onClick={() => save.mutate()}><ClipboardCheck /> {t("admin:governance.risks.record")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Whether each AI function serves Arabic as well as English, measured every quarter on its drafts. */
export function LanguageParityTab() {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const api = useApi();
  const qc = useQueryClient();
  const parity = useLanguageParity();
  const run = useSave({
    mutationFn: () => api.post<{ findings: number; passed: boolean }>("/api/governance/parity/run", {}),
    saved: (r) => ({ title: t("admin:governance.parity.ran"), description: r.passed ? t("admin:governance.parity.passed") : t("admin:governance.parity.failed", { count: r.findings }) }),
    failed: t("misc.error"),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ["governance"] }); },
  });
  if (parity.isLoading || !parity.data) return <Skeleton className="h-64" />;
  const p = parity.data;
  const l = p.latest;
  const summary = l ? t("admin:governance.parity.summary", { date: formatDate(l.ranAt, locale), by: l.ranBy, count: l.proposals, next: formatDate(l.nextDueAt, locale) }) : null;
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <p className="max-w-3xl text-sm text-muted-foreground">{t("admin:governance.parity.hint", { days: p.intervalDays, min: p.thresholds.minSample })}</p>
        <div className="flex items-center gap-2">
          {l && p.due ? <Badge variant="warning" dot>{t("admin:governance.parity.due")}</Badge> : null}
          {p.canRun ? <Button variant="gold" loading={run.isPending} onClick={() => run.mutate()}><Play /> {t("admin:governance.parity.run")}</Button> : null}
        </div>
      </div>
      {!l
        ? <InlineAlert tone="info" title={t("admin:governance.parity.notRun")}>{t("admin:governance.parity.notRunHint", { days: p.intervalDays })}</InlineAlert>
        : l.passed
          ? <InlineAlert tone="success" title={t("admin:governance.parity.passed")}>{summary}</InlineAlert>
          : <InlineAlert tone="danger" title={t("admin:governance.parity.failed", { count: l.findings })}>{summary}</InlineAlert>}
      {!p.canRun ? <p className="text-xs text-muted-foreground">{t("admin:governance.parity.runnerOnly")}</p> : null}
      {l ? (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">{t("admin:governance.parity.table")}</CardTitle>
            <CardDescription>
              {t("admin:governance.parity.window", { from: formatDate(l.windowFrom, locale), to: formatDate(l.windowTo, locale) })}{" "}
              {t("admin:governance.parity.thresholds", { coverage: p.thresholds.minCoverage, figures: Math.round(p.thresholds.minFigureParity * 100), gap: p.thresholds.maxRejectionGap, missing: Math.round(p.thresholds.maxMissingArabic * 100) })}
            </CardDescription>
          </CardHeader>
          <CardContent className="overflow-x-auto">
            {l.results.length === 0 ? <EmptyState icon={Languages} title={t("admin:governance.parity.noDrafts")} /> : (
              <table className="w-full text-sm">
                <thead className="text-xs uppercase tracking-wider text-sand-600">
                  <tr className="border-b border-sand-200 [&>th]:px-3 [&>th]:py-2 [&>th]:text-start">
                    <th>{t("admin:governance.parity.agent")}</th><th>{t("admin:governance.parity.drafts")}</th><th>{t("admin:governance.parity.coverage")}</th><th>{t("admin:governance.parity.figures")}</th>
                    <th>{t("admin:governance.parity.rejected")}</th><th>{t("admin:governance.parity.missing")}</th><th>{t("admin:governance.parity.findingsColumn")}</th>
                  </tr>
                </thead>
                <tbody>
                  {l.results.map((r) => (
                    <tr key={r.agentKey} className={cn("border-b border-sand-100 [&>td]:px-3 [&>td]:py-2", r.findings.length > 0 && "bg-danger-50/40")}>
                      <td className="font-medium">{locale === "ar" ? r.agentNameAr : r.agentName}</td>
                      <td className="tabular text-xs">{r.drafts} <span className="text-muted-foreground">({t("admin:governance.parity.bilingual", { count: r.bilingual })})</span></td>
                      <td className="tabular">{r.coverage != null ? `${r.coverage.toFixed(2)}×` : "—"}</td>
                      {/* Beside the parity mark of the check, in full where a whole percentage would put it on the mark (condition 2.9). */}
                      <td className="tabular">{r.figureParity == null ? "—" : `${formatNumberNear(r.figureParity * 100, locale, p.thresholds.minFigureParity * 100, 0)}%`}</td>
                      <td className="tabular text-xs">
                        {r.reviewedArabic + r.reviewedEnglish === 0 ? "—" : (
                          <>
                            {r.rejectedArabic != null ? `${r.rejectedArabic}%` : "—"} / {r.rejectedEnglish != null ? `${r.rejectedEnglish}%` : "—"}{" "}
                            <span className="text-muted-foreground">({r.reviewedArabic} / {r.reviewedEnglish})</span>
                          </>
                        )}
                      </td>
                      <td className="tabular text-xs">{r.arabicFiles > 0 ? `${r.missingArabic} / ${r.arabicFiles}` : "—"}</td>
                      <td>
                        {r.findings.length === 0
                          ? <Badge variant="success" size="sm">{t("admin:governance.parity.none")}</Badge>
                          : <div className="flex flex-wrap gap-1">{r.findings.map((f) => <Badge key={f} variant="danger" size="sm">{t(`admin:governance.parity.findings.${f}`, { defaultValue: f })}</Badge>)}</div>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>
      ) : null}
      {p.history.length > 1 ? (
        <Card className="p-4">
          <p className="text-sm font-semibold">{t("admin:governance.parity.history")}</p>
          <ul className="mt-2 space-y-1 text-xs">
            {p.history.map((h) => (
              <li key={h.id} className="flex items-center gap-2">
                <span className="tabular">{formatDateTime(h.ranAt, locale)}</span>
                <span className="text-muted-foreground">{t("admin:governance.parity.draftsRead", { count: h.proposals })}</span>
                {h.passed ? <Badge variant="success" size="sm">{t("admin:governance.parity.none")}</Badge> : <Badge variant="danger" size="sm">{t("admin:governance.parity.findingCount", { count: h.findings })}</Badge>}
              </li>
            ))}
          </ul>
        </Card>
      ) : null}
    </div>
  );
}

/** Whether officers decide or adopt: drafts approved unchanged, edited or rejected, by AI function and by officer. */
export function AcceptanceTab() {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const [days, setDays] = useState(180);
  const acceptance = useAiAcceptance(days);
  if (acceptance.isError) return <InlineAlert tone="info">{t("admin:governance.adoption.forbidden")}</InlineAlert>;
  if (acceptance.isLoading || !acceptance.data) return <Skeleton className="h-64" />;
  const a = acceptance.data;
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <p className="max-w-3xl text-sm text-muted-foreground">{t("admin:governance.adoption.hint", { min: a.thresholds.minimumReasonLength })}</p>
        <Field label={t("admin:governance.adoption.window")}>
          {(id) => (
            <NativeSelect id={id} value={String(days)} onChange={(e) => setDays(Number(e.target.value))}>
              {[90, 180, 365].map((d) => <option key={d} value={d}>{t("admin:governance.adoption.days", { count: d })}</option>)}
            </NativeSelect>
          )}
        </Field>
      </div>
      <p className="text-xs text-muted-foreground">
        {t("admin:governance.adoption.reviewed", { count: a.reviewed, from: formatDate(a.from, locale), to: formatDate(a.to, locale) })}{" "}
        {t("admin:governance.adoption.thresholds", { rate: Math.round(a.thresholds.unchangedAlert * 100), reviews: a.thresholds.minReviews, repeated: a.thresholds.repeatedAlert })}
      </p>
      {a.reviewed === 0 ? <EmptyState icon={ClipboardCheck} title={t("admin:governance.adoption.noReviews")} /> : (
        <>
          <AcceptanceTable title={t("admin:governance.adoption.functions")} rows={a.functions} kind="function" unchangedAlert={a.thresholds.unchangedAlert} />
          <AcceptanceTable title={t("admin:governance.adoption.officers")} rows={a.officers} kind="officer" unchangedAlert={a.thresholds.unchangedAlert} />
        </>
      )}
    </div>
  );
}

function AcceptanceTable({ title, rows, kind, unchangedAlert }: { title: string; rows: Array<AcceptanceFunctionRow | AcceptanceOfficerRow>; kind: "function" | "officer"; unchangedAlert: number }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const pct = (v?: number | null) => (v == null ? "—" : `${Math.round(v * 100)}%`);
  // The share approved unchanged beside the alert mark, in full where a whole percentage would put it on the mark (condition 2.9).
  const unchanged = (v?: number | null) => (v == null ? "—" : `${formatNumberNear(v * 100, locale, unchangedAlert * 100, 0)}%`);
  return (
    <Card>
      <CardHeader className="pb-2"><CardTitle className="text-base">{title}</CardTitle></CardHeader>
      <CardContent className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-xs uppercase tracking-wider text-sand-600">
            <tr className="border-b border-sand-200 [&>th]:px-3 [&>th]:py-2 [&>th]:text-start">
              <th>{kind === "function" ? t("admin:governance.adoption.function") : t("admin:governance.adoption.officer")}</th>
              {kind === "function" ? <th>{t("admin:governance.adoption.level")}</th> : null}
              <th>{t("admin:governance.adoption.reviewedColumn")}</th><th>{t("admin:governance.adoption.unchanged")}</th><th>{t("admin:governance.adoption.edited")}</th>
              <th>{t("admin:governance.adoption.rejected")}</th><th>{t("admin:governance.adoption.medianEdit")}</th>
              {kind === "officer" ? <th>{t("admin:governance.adoption.repeated")}</th> : null}
              <th>{t("admin:governance.adoption.findingsColumn")}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.key} className={cn("border-b border-sand-100 [&>td]:px-3 [&>td]:py-2", r.findings.length > 0 && "bg-warning-50/50")}>
                <td className="font-medium">{"level" in r ? (locale === "ar" && r.nameAr ? r.nameAr : r.name) : r.name}</td>
                {"level" in r ? <td><Badge size="sm" variant={levelTone[r.level]}>{t(`admin:governance.risks.levels.${r.level}`, { defaultValue: r.level })}</Badge></td> : null}
                <td className="tabular">{r.reviewed}</td>
                <td className="tabular">{r.unchanged} <span className="text-xs text-muted-foreground">({unchanged(r.unchangedRate)})</span></td>
                <td className="tabular">{r.edited}</td>
                <td className="tabular">{r.rejected}</td>
                <td className="tabular">{pct(r.medianEditDistance)}</td>
                {"repeatedReasons" in r ? <td className="tabular">{r.repeatedReasons || "—"}</td> : null}
                <td>
                  {r.findings.length === 0
                    ? <Badge variant="success" size="sm">{t("admin:governance.adoption.none")}</Badge>
                    : <div className="flex flex-wrap gap-1">{r.findings.map((f) => <Badge key={f} variant="warning" size="sm"><AlertTriangle /> {t(`admin:governance.adoption.findings.${f}`)}</Badge>)}</div>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
}
